import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  IconButton,
  OutlinedInput,
  Chip,
  FormHelperText,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CloudUpload as CloudUploadIcon, FileCopy as FileCopyIcon, Info as InfoIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import config from '../../config';
import './Advertisements.css';

// 画像URLを正規化する関数
const normalizeImageUrl = (url) => {
  if (!url) return '';
  
  // 既にフルURLの場合はそのまま返す
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // dataURLの場合もそのまま返す
  if (url.startsWith('data:')) {
    return url;
  }
  
  // 相対パスの場合、API_BASE_URLと組み合わせる
  // パスが/から始まる場合は、そのまま結合
  if (url.startsWith('/')) {
    return `${config.API_BASE_URL}${url}`;
  }
  
  // それ以外の場合は/を追加して結合
  return `${config.API_BASE_URL}/${url}`;
};

// 3時間区切りの時間帯オプション
const TIME_SLOTS = [
  { id: 0, label: '0:00 - 3:00', value: '0-3' },
  { id: 1, label: '3:00 - 6:00', value: '3-6' },
  { id: 2, label: '6:00 - 9:00', value: '6-9' },
  { id: 3, label: '9:00 - 12:00', value: '9-12' },
  { id: 4, label: '12:00 - 15:00', value: '12-15' },
  { id: 5, label: '15:00 - 18:00', value: '15-18' },
  { id: 6, label: '18:00 - 21:00', value: '18-21' },
  { id: 7, label: '21:00 - 24:00', value: '21-24' }
];

// タブパネルのコンポーネント
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ad-tabpanel-${index}`}
      aria-labelledby={`ad-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// 広告の掲載期間を計算する関数
const calculateAdPeriod = () => {
  // 日本時間での現在時刻を取得（UTC+9）
  const now = new Date();
  const japanTime = new Date(now.getTime() + (9 * 60 * 60 * 1000));
  
  const currentYear = japanTime.getFullYear();
  const currentMonth = japanTime.getMonth();
  const currentDay = japanTime.getDate();
  
  console.log('広告期間計算:', {
    current_japan_time: japanTime.toISOString(),
    current_day: currentDay,
    current_month: currentMonth + 1,
    current_year: currentYear
  });
  
  let startDate, endDate;
  
  // 21日以降の場合は、現在の月の21日から翌月20日まで
  // 20日以前の場合は、前月の21日から現在の月の20日まで
  if (currentDay >= 21) {
    // 21日以降：今月21日～翌月20日
    startDate = new Date(currentYear, currentMonth, 21);
    endDate = new Date(currentYear, currentMonth + 1, 20, 23, 59, 59);
  } else {
    // 20日以前：前月21日～今月20日
    startDate = new Date(currentYear, currentMonth - 1, 21);
    endDate = new Date(currentYear, currentMonth, 20, 23, 59, 59);
  }
  
  console.log('計算された広告期間:', {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  });
  
  return {
    startDate,
    endDate
  };
};

// 次回の掲載期間を計算する関数（複製用）
const calculateNextAdPeriod = (adEndDate) => {
  let baseEndDate;
  
  if (adEndDate) {
    // 広告データの終了日がある場合はそれを基準にする
    baseEndDate = new Date(adEndDate);
  } else {
    // なければ現在の期間の終了日を使用
    const { endDate } = calculateAdPeriod();
    baseEndDate = new Date(endDate);
  }
  
  // 次の期間は終了日の翌日（21日）から始まる
  const nextStartDate = new Date(baseEndDate);
  nextStartDate.setDate(nextStartDate.getDate() + 1);
  nextStartDate.setHours(0, 0, 0, 0);
  
  // 終了日は翌月の20日
  const nextEndDate = new Date(nextStartDate);
  nextEndDate.setMonth(nextEndDate.getMonth() + 1);
  nextEndDate.setDate(20);
  nextEndDate.setHours(23, 59, 59);
  
  return {
    startDate: nextStartDate,
    endDate: nextEndDate
  };
};

// 日付をフォーマットする関数
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// 広告のステータスを取得する関数を修正
const getAdStatus = (ad) => {
  // すでに削除済みの場合はそのまま返す
  if (ad.status === 'deleted') {
    return 'deleted';
  }
  
  // 現在時刻（ローカルタイム = JST）
  const now = new Date();
  
  // 開始日時と終了日時を取得（複数のフィールド名に対応）
  const startDateValue = ad.startDate || ad.startTime;
  const endDateValue = ad.endDate || ad.endTime;
  
  if (!startDateValue || !endDateValue) {
    return ad.status; // 期間情報がない場合は元のステータスを返す
  }
  
  const startDate = new Date(startDateValue);
  
  // endDate はその日の終わり（23:59:59 ローカル時間）まで有効
  const endDateRaw = new Date(endDateValue);
  const endDate = new Date(endDateRaw.getFullYear(), endDateRaw.getMonth(), endDateRaw.getDate(), 23, 59, 59, 999);
  
  // 将来の開始日を持つ広告は「予約」と表示
  if (startDate > now) {
    return 'reserved';
  }
  
  // 終了日を過ぎた広告は「終了」と表示
  if (now > endDate) {
    return 'ended';
  }
  
  // 現在は掲載期間内。停止中など明示的な非稼働ステータスはそのまま尊重し、
  // それ以外（active / reserved / 空 など）は「配信中(active)」として扱う。
  // ※ 保存済みの status が 'reserved' のままでも、開始日を過ぎていれば配信中と表示する
  const inactiveStatuses = ['inactive', 'paused', 'stopped', 'disabled'];
  if (inactiveStatuses.includes(ad.status)) {
    return ad.status;
  }
  return 'active';
};

// 広告ステータスのラベルを取得する関数を修正
const getStatusLabel = (status) => {
  switch(status) {
    case 'active':
      return '配信中';
    case 'inactive':
      return '停止中';
    case 'deleted':
      return '削除済';
    case 'reserved':
      return '予約'; // 表示用ステータス
    case 'ended':
      return '終了'; // 表示用ステータス
    default:
      return status;
  }
};

// 市町村コードから市町村名を取得する関数
const getCityNameByCode = (cityCode, citiesList) => {
  if (!cityCode || !citiesList || citiesList.length === 0) return cityCode;
  
  const city = citiesList.find(city => city.code === cityCode);
  return city ? city.name : cityCode;
};

// 広告の並び順を最新のものが上になるように並べ替える関数
const sortAdsByNewest = (ads) => {
  return [...ads].sort((a, b) => {
    const aDate = new Date(a.created_at || a.startTime || a.startDate || 0);
    const bDate = new Date(b.created_at || b.startTime || b.startDate || 0);
    return bDate - aDate; // 降順（新しいものが上）
  });
};

const Advertisements = () => {
  const { token, currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  // 広告データの状態
  const [areaTopAds, setAreaTopAds] = useState([]);
  const [infeedAds, setInfeedAds] = useState([]);
  
  // 選択された広告
  const [selectedAd, setSelectedAd] = useState(null);
  
  // ダイアログの状態
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'create', 'edit', 'delete'
  const [dialogAdType, setDialogAdType] = useState(''); // 'areaTop', 'infeed'
  
  // 地域選択用データ
  const [prefectures, setPrefectures] = useState([]);
  const [cities, setCities] = useState([]);
  const [userCities, setUserCities] = useState([]);
  
  // 画像アップロード
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  
  // 新規広告/編集広告のフォームデータ
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    targetUrl: '',
    status: 'active',
    area: '',
    timeSlots: [], // 3時間区切りの時間帯（複数選択可）
    budget: 5000,   // 予算（円）
    bidAmount: 100,  // クリック単価（円）
    additionalBudget: 0
  });

  // 広告複製用のダイアログ状態
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateAdData, setDuplicateAdData] = useState(null);
  const [inheritBudget, setInheritBudget] = useState(false);
  
  // 広告費用データの状態
  const [billingData, setBillingData] = useState({
    current_month: {
      amount: 0,
      is_confirmed: false,
      period_start: '',
      period_end: '',
      detail: []
    },
    next_month: {
      amount: 0,
      period_start: '',
      period_end: '',
      detail: []
    }
  });

  // 費用詳細ダイアログの状態
  const [billingDetailOpen, setBillingDetailOpen] = useState(false);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState('current'); // 'current' or 'next'

  // 新しい状態を追加
  const [highestBids, setHighestBids] = useState({});

  // 文字数制限の定数
  const MAX_TITLE_LENGTH = 16;
  const MAX_DESCRIPTION_LENGTH = 250;
  
  // 文字数をカウントする関数（マルチバイト文字対応）
  const getCharacterCount = (text) => {
    if (!text) return 0;
    // 日本語などのマルチバイト文字も1文字として正確にカウント
    return [...text].length;
  };
  
  // 文字数関連の計算
  const titleCharCount = getCharacterCount(formData.title);
  const descriptionCharCount = getCharacterCount(formData.description);
  const titleRemainingChars = MAX_TITLE_LENGTH - titleCharCount;
  const descriptionRemainingChars = MAX_DESCRIPTION_LENGTH - descriptionCharCount;
  const isTitleOverLimit = titleCharCount > MAX_TITLE_LENGTH;
  const isDescriptionOverLimit = descriptionCharCount > MAX_DESCRIPTION_LENGTH;

  // タブの切り替え処理
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // 都道府県・市区町村データの取得
  useEffect(() => {
    const fetchPrefecturesData = async () => {
      try {
              console.log('都道府県データAPI呼び出し:', `${config.API_BASE_URL}/api/areas/prefectures`);
      const response = await axios.get(`${config.API_BASE_URL}/api/areas/prefectures`);
        console.log('都道府県データAPIレスポンス:', response);
        setPrefectures(response.data || []);
      } catch (err) {
        console.error('=== 都道府県データ取得エラー詳細 ===');
        console.error('エラーオブジェクト:', err);
        console.error('エラーメッセージ:', err.message);
        console.error('エラーステータス:', err.response?.status);
        console.error('エラーステータステキスト:', err.response?.statusText);
        console.error('エラーレスポンスデータ:', err.response?.data);
        console.error('リクエストURL:', err.config?.url);
        console.error('=== 都道府県データ取得エラー詳細終了 ===');
      }
    };
    
    fetchPrefecturesData();
  }, []);
  
  // 企業ユーザーのプロフィール情報（登録エリア）を取得
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!token) return;
      
      try {
        console.log('ユーザープロフィールAPI呼び出し:', `${config.API_BASE_URL}/api/corporate/profile`);
        const response = await axios.get(`${config.API_BASE_URL}/api/corporate/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        console.log('ユーザープロフィールAPIレスポンス:', response);
        
        if (response.data && response.data.status === 'success') {
          const userData = response.data.data;
          
          // 企業ユーザーの登録市区町村を設定
          if (userData.cities && userData.cities.length > 0) {
            setUserCities(userData.cities);
          }
          
          // 企業ユーザーの都道府県を選んだ場合、市区町村リストを取得
          if (userData.prefecture?.code) {
            fetchCitiesData(userData.prefecture.code);
          }
        }
      } catch (err) {
        console.error('=== ユーザープロフィール取得エラー詳細 ===');
        console.error('エラーオブジェクト:', err);
        console.error('エラーメッセージ:', err.message);
        console.error('エラーステータス:', err.response?.status);
        console.error('エラーステータステキスト:', err.response?.statusText);
        console.error('エラーレスポンスデータ:', err.response?.data);
        console.error('リクエストURL:', err.config?.url);
        console.error('=== ユーザープロフィール取得エラー詳細終了 ===');
      }
    };
    
    fetchUserProfile();
  }, [token]);

  // 広告費用データを取得する関数
  const fetchBillingData = async () => {
    try {
              console.log('請求データAPI呼び出し:', `${config.API_BASE_URL}/api/corporate/ads/billing`);
        const response = await axios.get(`${config.API_BASE_URL}/api/corporate/ads/billing`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      console.log('請求データAPIレスポンス:', response);
      
      if (response.data && response.data.status === 'success') {
        setBillingData({
          current_month: response.data.current_month,
          next_month: response.data.next_month
        });
      }
    } catch (err) {
      console.error('=== 請求データ取得エラー詳細 ===');
      console.error('エラーオブジェクト:', err);
      console.error('エラーメッセージ:', err.message);
      console.error('エラーステータス:', err.response?.status);
      console.error('エラーステータステキスト:', err.response?.statusText);
      console.error('エラーレスポンスデータ:', err.response?.data);
      console.error('リクエストURL:', err.config?.url);
      console.error('=== 請求データ取得エラー詳細終了 ===');
    }
  };

  // 広告データの取得
  useEffect(() => {
    const fetchAdsData = async () => {
      try {
        setLoading(true);
        console.log('=== 広告データ取得開始 ===');
        
        // エリアトップ広告を取得
        console.log('エリアトップ広告API呼び出し:', `${config.API_BASE_URL}/api/corporate/ads/areatop`);
        const areaTopResponse = await axios.get(`${config.API_BASE_URL}/api/corporate/ads/areatop`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });

        console.log('エリアトップ広告APIレスポンス:', areaTopResponse);
        
        if (areaTopResponse.data && areaTopResponse.data.status === 'success') {
          // 画像URLを正規化して設定
          const normalizedAds = areaTopResponse.data.ads.map(ad => ({
            ...ad,
            imageUrl: normalizeImageUrl(ad.imageUrl)
          }));
          
          // 広告の期間情報をデバッグ出力
          console.log('=== エリアトップ広告の期間情報 ===');
          normalizedAds.slice(0, 3).forEach((ad, index) => {
            console.log(`広告${index + 1} (ID: ${ad.id}):`, {
              title: ad.title,
              status: ad.status,
              startDate: ad.startDate,
              startTime: ad.startTime,
              endDate: ad.endDate,
              endTime: ad.endTime,
              created_at: ad.created_at,
              createdAt: ad.createdAt
            });
          });
          
          // 最新順に並べ替えて設定
          setAreaTopAds(sortAdsByNewest(normalizedAds) || []);
        }
        
        // インフィード広告を取得
        console.log('インフィード広告API呼び出し:', `${config.API_BASE_URL}/api/corporate/ads/infeed`);
        const infeedResponse = await axios.get(`${config.API_BASE_URL}/api/corporate/ads/infeed`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        console.log('インフィード広告APIレスポンス:', infeedResponse);
        
        if (infeedResponse.data && infeedResponse.data.status === 'success') {
          // 画像URLを正規化して設定
          const normalizedAds = infeedResponse.data.ads.map(ad => ({
            ...ad,
            imageUrl: normalizeImageUrl(ad.imageUrl)
          }));
          
          // 広告の期間情報をデバッグ出力
          console.log('=== インフィード広告の期間情報 ===');
          normalizedAds.slice(0, 3).forEach((ad, index) => {
            console.log(`広告${index + 1} (ID: ${ad.id}):`, {
              title: ad.title,
              status: ad.status,
              startDate: ad.startDate,
              startTime: ad.startTime,
              endDate: ad.endDate,
              endTime: ad.endTime,
              created_at: ad.created_at,
              createdAt: ad.createdAt
            });
          });
          
          // 最新順に並べ替えて設定
          setInfeedAds(sortAdsByNewest(normalizedAds) || []);
        }
        
        // 広告費用データを取得
        await fetchBillingData();
        
        // 広告データ取得後、全エリアの最高単価を取得
        if (userCities.length > 0) {
          
          for (const city of userCities) {
            await fetchHighestBid(city.code);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('=== 広告データ取得エラー詳細 ===');
        console.error('エラーオブジェクト:', err);
        console.error('エラーメッセージ:', err.message);
        console.error('エラーステータス:', err.response?.status);
        console.error('エラーステータステキスト:', err.response?.statusText);
        console.error('エラーレスポンスデータ:', err.response?.data);
        console.error('エラーレスポンスヘッダー:', err.response?.headers);
        console.error('リクエスト設定:', err.config);
        console.error('リクエストURL:', err.config?.url);
        console.error('リクエストメソッド:', err.config?.method);
        console.error('リクエストヘッダー:', err.config?.headers);
        console.error('リクエストデータ:', err.config?.data);
        console.error('ネットワークエラー:', err.code);
        console.error('=== エラー詳細終了 ===');
        
        setError(`広告データの取得に失敗しました - ${err.response?.status}: ${err.response?.statusText || err.message}`);
        setLoading(false);
      }
    };
    
    fetchAdsData();
  }, [token]);
  
  // 市区町村データの取得
  const fetchCitiesData = async (prefectureCode) => {
    if (!prefectureCode) return;
    
    try {
      console.log('市区町村データAPI呼び出し:', `${config.API_BASE_URL}/api/areas/cities/${prefectureCode}`);
      const response = await axios.get(`${config.API_BASE_URL}/api/areas/cities/${prefectureCode}`);
      console.log('市区町村データAPIレスポンス:', response);
      setCities(response.data || []);
    } catch (err) {
      console.error('=== 市区町村データ取得エラー詳細 ===');
      console.error('エラーオブジェクト:', err);
      console.error('エラーメッセージ:', err.message);
      console.error('エラーステータス:', err.response?.status);
      console.error('エラーステータステキスト:', err.response?.statusText);
      console.error('エラーレスポンスデータ:', err.response?.data);
      console.error('リクエストURL:', err.config?.url);
      console.error('=== 市区町村データ取得エラー詳細終了 ===');
    }
  };
  
  // モックデータの生成（開発用）
  const generateMockAreaTopAds = () => {
    return Array(5).fill().map((_, i) => ({
      id: `areaTop-${i+1}`,
      title: `エリアトップ広告 ${i+1}`,
      description: `エリアトップ広告の説明文 ${i+1}`,
      imageUrl: `https://picsum.photos/800/200?random=${i+1}`,
      targetUrl: 'https://example.com',
      status: i % 3 === 0 ? 'inactive' : 'active',
      timeSlots: ['0-3', '12-15', '18-21'],
      budget: 5000 + i * 1000,
      bidAmount: 100 + i * 10,
      area: `エリア${i+1}`,
      impressions: Math.floor(Math.random() * 10000) + 1000,
      clicks: Math.floor(Math.random() * 1000) + 100,
    }));
  };
  
  const generateMockInfeedAds = () => {
    return Array(8).fill().map((_, i) => ({
      id: `infeed-${i+1}`,
      title: `インフィード広告 ${i+1}`,
      description: `インフィード広告の説明文 ${i+1}`,
      imageUrl: `https://picsum.photos/400/300?random=${i+10}`,
      targetUrl: 'https://example.com',
      status: i % 4 === 0 ? 'inactive' : 'active',
      timeSlots: ['6-9', '15-18', '21-24'],
      budget: 3000 + i * 500,
      bidAmount: 80 + i * 5,
      position: i + 1,
      impressions: Math.floor(Math.random() * 20000) + 5000,
      clicks: Math.floor(Math.random() * 2000) + 500,
    }));
  };
  
  // 画像ファイル選択処理
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // ファイルサイズチェック (2MB以下)
    if (file.size > 2 * 1024 * 1024) {
      setError('画像サイズが大きすぎます。2MB以下の画像を選択してください。');
      return;
    }
    
    setImageFile(file);
    
    // プレビュー表示用にURLを生成
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  // 画像アップロード処理（TweetBox方式に統一）
  const uploadImage = async () => {
    if (!imageFile) return null;
    
    try {
      // TweetBoxと同じ方式でBase64に変換
      const reader = new FileReader();
      
      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          try {
            const base64Data = e.target.result;
            
            // ファイルサイズチェック
            const blob = await fetch(base64Data).then(r => r.blob());
            if (blob.size > 5 * 1024 * 1024) {
              throw new Error('画像ファイルサイズが5MBを超えています');
            }
            
            // ファイルタイプを取得
            let fileType = "image/jpeg";
            if (base64Data.startsWith('data:image/png')) {
              fileType = "image/png";
            } else if (base64Data.startsWith('data:image/gif')) {
              fileType = "image/gif";
            } else if (base64Data.startsWith('data:image/webp')) {
              fileType = "image/webp";
            }
            
            console.log('広告画像アップロード:', {
              type: fileType,
              sizeInMB: (blob.size / (1024 * 1024)).toFixed(2),
              dataUrlLength: base64Data.length
            });
            
            // TweetBoxと同じ形式でAPIに送信（api.phpがBase64→ファイル変換を処理）
            const response = await axios.post(
              `${config.API_BASE_URL}/api/corporate/upload-image`,
              {
                image_data: base64Data,
                image_type: fileType,
                type: 'ad'
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            console.log('画像アップロードAPIレスポンス:', response);
            
            if (response.data && response.data.status === 'success') {
              // レスポンスからURLを取得し、正規化
              const imageUrl = response.data.image_url || response.data.url;
              resolve(normalizeImageUrl(imageUrl));
            } else {
              console.error('画像アップロード失敗: レスポンスステータスが成功ではありません');
              resolve(null);
            }
          } catch (error) {
            console.error('=== 画像アップロードエラー詳細 ===');
            console.error('エラーオブジェクト:', error);
            console.error('エラーメッセージ:', error.message);
            console.error('エラーステータス:', error.response?.status);
            console.error('エラーステータステキスト:', error.response?.statusText);
            console.error('エラーレスポンスデータ:', error.response?.data);
            console.error('リクエストURL:', error.config?.url);
            console.error('=== 画像アップロードエラー詳細終了 ===');
            resolve(null);
          }
        };
        
        reader.onerror = () => {
          console.error('ファイル読み込みエラー');
          resolve(null);
        };
        
        reader.readAsDataURL(imageFile);
      });
    } catch (error) {
      console.error('画像アップロード処理エラー:', error);
      return null;
    }
  };
  
  // ダイアログを開く処理を修正
  const handleOpenDialog = (type, adType, ad = null) => {
    setDialogType(type);
    setDialogAdType(adType);
    setSelectedAd(ad);
    setError('');
    
    if (type === 'edit' && ad) {
      console.log('=== 編集ダイアログ開始 ===');
      console.log('編集対象の広告データ:', ad);
      
      // 時間帯の適切な変換処理
      let timeSlots = [];
      if (ad.timeSlots) {
        // 文字列またはカンマ区切りの値を配列に変換
        if (typeof ad.timeSlots === 'string') {
          timeSlots = ad.timeSlots.split(',').map(slot => slot.trim()).filter(Boolean);
        } else if (Array.isArray(ad.timeSlots)) {
          timeSlots = [...ad.timeSlots];
        }
      }
      
      console.log('変換された時間帯:', timeSlots);
      
      // 表示用ステータスがreservedやendedの場合でも、APIに送信するステータスはactive/inactiveに変換
      let apiStatus = ad.status;
      if (getAdStatus(ad) === 'reserved' || getAdStatus(ad) === 'ended') {
        // 予約中や終了は内部的にはactive扱い
        apiStatus = 'active';
      }
      
      const newFormData = {
        title: ad.title || '',
        description: ad.description || '',
        targetUrl: ad.targetUrl || '',
        area: ad.area || '',
        timeSlots: timeSlots,
        budget: ad.budget || 5000,
        bidAmount: ad.bidAmount || 100,
        status: apiStatus, // API用のステータス
        additionalBudget: 0 // 追加予算は初期値0
      };
      
      console.log('設定するフォームデータ:', newFormData);
      
      setFormData(newFormData);
      
      // 画像プレビュー設定
      if (ad.imageUrl) {
        setImagePreview(ad.imageUrl);
      }
    } else {
      // 新規作成時
      setFormData({
        title: '',
        description: '',
        targetUrl: '',
        area: userCities.length > 0 ? userCities[0].code : '',
        timeSlots: [],
        budget: 5000,
        bidAmount: 100,
        status: 'active',
        additionalBudget: 0
      });
      
      setImagePreview('');
      setImageFile(null);
    }
    
    setDialogOpen(true);
  };
  
  // ダイアログを閉じる処理
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError(null);
  };
  
  // フォームの入力処理
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // 文字数制限チェック
    if (name === 'title' && getCharacterCount(value) > MAX_TITLE_LENGTH) {
      // タイトルが制限を超える場合は更新しない
      return;
    }
    
    if (name === 'description' && getCharacterCount(value) > MAX_DESCRIPTION_LENGTH) {
      // 説明が制限を超える場合は更新しない
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // エリアが変更された場合は最高単価を取得
    if (name === 'area' && value) {
      fetchHighestBid(value);
    }
  };
  
  // 時間帯選択の処理を修正
  const handleTimeSlotChange = (event) => {
    const { target: { value } } = event;
    
    // 選択された時間帯を配列として処理（Material UIのSelectは配列を返す）
    const selectedTimeSlots = typeof value === 'string' ? value.split(',') : value;
    
    
    
    // 更新されたフォームデータを設定
    setFormData({
      ...formData,
      timeSlots: selectedTimeSlots
    });
  };
  
  // 広告複製ダイアログを開く
  const openDuplicateDialog = (ad) => {
    setDuplicateAdData(ad);
    setInheritBudget(false);
    setDuplicateDialogOpen(true);
  };

  // 広告複製ダイアログを閉じる
  const closeDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
    setDuplicateAdData(null);
  };

  // 広告を複製する処理
  const handleDuplicateAd = async (ad) => {
    openDuplicateDialog(ad);
  };
  
  // 広告複製を実行する処理
  const executeDuplicateAd = async () => {
    try {
      setLoading(true);
      
      if (!duplicateAdData) {
        setError('複製元の広告データがありません。');
        setLoading(false);
        return;
      }
      
      // 複製元の広告データを取得
      const duplicatedAdData = {
        title: duplicateAdData.title,
        description: duplicateAdData.description,
        targetUrl: duplicateAdData.targetUrl,
        area: duplicateAdData.area,
        timeSlots: duplicateAdData.timeSlots,
        budget: duplicateAdData.budget,
        bidAmount: duplicateAdData.bidAmount,
        imageUrl: duplicateAdData.imageUrl,
        status: 'reserved', // 複製時は予約状態に設定
        parentAdId: duplicateAdData.id, // 親広告のID
        inheritBudget: inheritBudget // 予算を引き継ぐかどうか
      };
      
      // 次回の掲載期間を計算
      const { startDate, endDate } = calculateNextAdPeriod(duplicateAdData.endDate);
      
      // API用にデータを整形し、期間情報を追加
      const apiData = {
        ...duplicatedAdData,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString()
      };
      
      // 広告タイプ（エリアトップかインフィードか）を判定
      // テーブルの表示内容から判断するか、広告データに含まれている場合はそれを使用
      const isAreaTopAd = (areaTopAds || []).some(a => a && a.id === duplicateAdData.id);
      
      // 広告タイプに応じたAPIエンドポイントを設定
      const apiUrl = isAreaTopAd
        ? `${config.API_BASE_URL}/api/corporate/ads/areatop`
        : `${config.API_BASE_URL}/api/corporate/ads/infeed`;
      
      // 新規広告として作成
      await axios.post(apiUrl, apiData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // 広告データを再取得
      if (isAreaTopAd) {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.status === 'success') {
          const normalizedAds = (response.data.ads || []).map(ad => ({
            ...ad,
            imageUrl: normalizeImageUrl(ad.imageUrl)
          }));
          setAreaTopAds(sortAdsByNewest(normalizedAds));
        }
      } else {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.status === 'success') {
          const normalizedAds = (response.data.ads || []).map(ad => ({
            ...ad,
            imageUrl: normalizeImageUrl(ad.imageUrl)
          }));
          setInfeedAds(sortAdsByNewest(normalizedAds));
        }
      }
      
      setLoading(false);
      closeDuplicateDialog();
      
      // 成功メッセージを表示
      alert(`広告が複製されました。${inheritBudget ? '（予算引継設定あり）' : ''}`);
    } catch (err) {
      console.error('=== 広告複製エラー詳細 ===');
      console.error('エラーオブジェクト:', err);
      console.error('エラーメッセージ:', err.message);
      console.error('エラーステータス:', err.response?.status);
      console.error('エラーステータステキスト:', err.response?.statusText);
      console.error('エラーレスポンスデータ:', err.response?.data);
      console.error('リクエストURL:', err.config?.url);
      console.error('リクエストメソッド:', err.config?.method);
      console.error('リクエストヘッダー:', err.config?.headers);
      console.error('=== 広告複製エラー詳細終了 ===');
      
      setError('広告の複製中にエラーが発生しました。');
      setLoading(false);
      closeDuplicateDialog();
    }
  };
  
  // 広告の保存処理
  const handleSaveAd = async () => {
    try {
      setLoading(true);
      
      // 必須入力フィールドのチェック
      if (!formData.title || !formData.targetUrl || !formData.area || formData.timeSlots.length === 0) {
        setError('必須項目をすべて入力してください。');
        setLoading(false);
        return;
      }
      
      // 文字数制限チェック
      if (getCharacterCount(formData.title) > MAX_TITLE_LENGTH) {
        setError(`タイトルは${MAX_TITLE_LENGTH}文字以内で入力してください。現在：${getCharacterCount(formData.title)}文字`);
        setLoading(false);
        return;
      }
      
      if (getCharacterCount(formData.description) > MAX_DESCRIPTION_LENGTH) {
        setError(`説明は${MAX_DESCRIPTION_LENGTH}文字以内で入力してください。現在：${getCharacterCount(formData.description)}文字`);
        setLoading(false);
        return;
      }
      
      // 画像アップロード
      let imageUrl = null;
      if (imageFile) {
        // 新しい画像ファイルがある場合はアップロード
        imageUrl = await uploadImage();
        if (!imageUrl) {
          setError('画像のアップロードに失敗しました。');
          setLoading(false);
          return;
        }
      } else if (imagePreview) {
        // 既存の画像またはプレビュー画像を使用
        if (selectedAd && dialogType === 'edit') {
          // 編集時は既存の画像URLを使用
          imageUrl = selectedAd.imageUrl;
        } else {
          // 新規作成時またはプレビューURLを使用
          imageUrl = imagePreview;
        }
      } else {
        setError('広告画像をアップロードしてください。');
        setLoading(false);
        return;
      }
      
      // 編集時は予算を加算する
      const finalBudget = dialogType === 'edit' && formData.additionalBudget 
        ? parseFloat(formData.budget) + parseFloat(formData.additionalBudget)
        : parseFloat(formData.budget);
      
      // 掲載期間を計算
      const { startDate, endDate } = calculateAdPeriod();
      
      // timeSlots処理 - 配列を確実にカンマ区切り文字列に変換
      let processedTimeSlots = formData.timeSlots;
      if (Array.isArray(formData.timeSlots)) {
        processedTimeSlots = formData.timeSlots.join(',');
      }
      
      // ステータス値を修正 - 予約（reserved）は有効なステータスではないため、activeに置き換える
      let status = formData.status;
      
      // 無効なステータス値をチェックして修正
      if (status !== 'active' && status !== 'inactive' && status !== 'deleted') {
        
        status = 'active';
      }
      
      
      
      const adData = {
        ...formData,
        status: status, // 修正したステータス値
        timeSlots: processedTimeSlots,
        imageUrl: imageUrl,
        budget: finalBudget,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString()
      };
      
      // additionalBudgetフィールドは送信データから削除
      if (adData.additionalBudget !== undefined) {
        delete adData.additionalBudget;
      }
      
      // デバッグ情報 - 送信データを出力
      console.log('=== 広告保存デバッグ情報 ===');
      console.log('dialogType:', dialogType);
      console.log('formData:', formData);
      console.log('processedTimeSlots:', processedTimeSlots);
      console.log('送信するadData:', adData);
      console.log('API URL:', dialogAdType === 'areaTop' 
        ? `${config.API_BASE_URL}/api/corporate/ads/areatop`
        : `${config.API_BASE_URL}/api/corporate/ads/infeed`);
      
      // 広告タイプに応じたAPIエンドポイントを設定
      const apiUrl = dialogAdType === 'areaTop' 
        ? `${config.API_BASE_URL}/api/corporate/ads/areatop`
        : `${config.API_BASE_URL}/api/corporate/ads/infeed`;
      
      if (dialogType === 'create') {
        // 新規作成
        await axios.post(apiUrl, adData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } else if (dialogType === 'edit') {
        // 編集
        console.log('=== 編集リクエスト送信 ===');
        console.log('PUT URL:', `${apiUrl}/${selectedAd.id}`);
        console.log('送信データ:', adData);
        
        const response = await axios.put(`${apiUrl}/${selectedAd.id}`, adData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('=== 編集レスポンス ===');
        console.log('レスポンス:', response.data);
      }
      
      // 広告データを再取得
      if (dialogAdType === 'areaTop') {
        console.log('=== エリアトップ広告データ再取得開始 ===');
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('=== 再取得レスポンス ===');
        console.log('レスポンスデータ:', response.data);
        
        // 画像URLを正規化して設定
        if (response.data && response.data.status === 'success') {
          const normalizedAds = response.data.ads.map(ad => ({
            ...ad,
            imageUrl: normalizeImageUrl(ad.imageUrl)
          }));
          console.log('=== 正規化後の広告データ ===');
          console.log('正規化されたデータ:', normalizedAds);
          
          // 編集した広告のデータを確認（編集時のみ）
          if (dialogType === 'edit' && selectedAd) {
            const editedAd = normalizedAds.find(ad => ad.id === selectedAd.id);
            if (editedAd) {
              console.log('=== 編集された広告の最新データ ===');
              console.log('タイトル:', editedAd.title);
              console.log('説明文:', editedAd.description);
              console.log('画像URL:', editedAd.imageUrl);
              console.log('時間帯:', editedAd.timeSlots);
            }
          }
          
          setAreaTopAds(sortAdsByNewest(normalizedAds));
        }
      } else {
        console.log('=== インフィード広告データ再取得開始 ===');
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('=== 再取得レスポンス ===');
        console.log('レスポンスデータ:', response.data);
        
        // 画像URLを正規化して設定
        if (response.data && response.data.status === 'success') {
          const normalizedAds = response.data.ads.map(ad => ({
            ...ad,
            imageUrl: normalizeImageUrl(ad.imageUrl)
          }));
          console.log('=== 正規化後の広告データ ===');
          console.log('正規化されたデータ:', normalizedAds);
          
          // 編集した広告のデータを確認（編集時のみ）
          if (dialogType === 'edit' && selectedAd) {
            const editedAd = normalizedAds.find(ad => ad.id === selectedAd.id);
            if (editedAd) {
              console.log('=== 編集された広告の最新データ ===');
              console.log('タイトル:', editedAd.title);
              console.log('説明文:', editedAd.description);
              console.log('画像URL:', editedAd.imageUrl);
              console.log('時間帯:', editedAd.timeSlots);
            }
          }
          
          setInfeedAds(sortAdsByNewest(normalizedAds));
        }
      }
      
      // 広告費用データを再取得（タイトル変更を反映するため）
      console.log('=== 広告費用データ再取得開始 ===');
      await fetchBillingData();
      console.log('=== 広告費用データ再取得完了 ===');
      
      // 成功メッセージを表示
      console.log('=== 広告保存完了 ===');
      if (dialogType === 'create') {
        console.log('新規広告が正常に作成されました');
      } else {
        console.log('広告が正常に更新されました');
      }
      
      setDialogOpen(false);
      setLoading(false);
    } catch (err) {
      console.log('=== 広告保存エラー詳細 ===');
      console.log('エラー:', err);
      console.log('レスポンス:', err.response);
      
      // エラー時のデータ再取得：実際にはデータベース保存が成功している可能性があるため確認
      console.log('=== エラー後のデータ再取得を試行 ===');
      try {
        // 広告データを再取得して実際に作成/更新されているか確認
        const apiUrl = dialogAdType === 'areaTop' 
          ? `${config.API_BASE_URL}/api/corporate/ads/areatop`
          : `${config.API_BASE_URL}/api/corporate/ads/infeed`;
        
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.status === 'success') {
          console.log('=== データ再取得成功 - 広告操作は実際に成功していました ===');
          
          // 画像URLを正規化して設定
          const normalizedAds = response.data.ads.map(ad => ({
            ...ad,
            imageUrl: normalizeImageUrl(ad.imageUrl)
          }));
          
          if (dialogAdType === 'areaTop') {
            setAreaTopAds(sortAdsByNewest(normalizedAds));
          } else {
            setInfeedAds(sortAdsByNewest(normalizedAds));
          }
          
          // 広告費用データを再取得
          await fetchBillingData();
          
          // 成功として扱い、エラーメッセージは表示しない
          console.log('=== 実際の広告操作は成功 - 正常終了 ===');
          if (dialogType === 'create') {
            console.log('新規広告が正常に作成されました（エラー後確認）');
          } else {
            console.log('広告が正常に更新されました（エラー後確認）');
          }
          
          setDialogOpen(false);
          setLoading(false);
          return; // エラー処理をスキップ
        }
      } catch (retryErr) {
        console.log('=== データ再取得も失敗 ===');
        console.log('再取得エラー:', retryErr);
              }
        
        // データ再取得でも失敗した場合のみエラーメッセージを表示
        console.log('=== 実際の失敗を確認 - エラーメッセージを表示 ===');
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          // 認証エラーの場合
          setError(`認証エラー (${err.response.status}): ${err.response.data.message || 'ログインし直してください。'}`);
        } else if (err.response && err.response.status >= 400 && err.response.status < 500) {
          // その他の400番台エラー
          setError(`エラー (${err.response.status}): ${err.response.data.message || '入力内容を確認してください。'}`);
        } else {
          // 500番台やネットワークエラー
          setError('広告の保存中にエラーが発生しました。データベースへの保存は完了していない可能性があります。');
        }
      
      setLoading(false);
      setDialogOpen(false);
    }
  };
  
  // 広告の削除処理
  const handleDeleteAd = async () => {
    try {
      setLoading(true);
      
      const apiUrl = dialogAdType === 'areaTop' 
        ? `${config.API_BASE_URL}/api/corporate/ads/areatop/${selectedAd.id}`
        : `${config.API_BASE_URL}/api/corporate/ads/infeed/${selectedAd.id}`;
      
      await axios.delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // 広告データの更新
      if (dialogAdType === 'areaTop') {
        setAreaTopAds(areaTopAds.filter(ad => ad.id !== selectedAd.id));
      } else {
        setInfeedAds(infeedAds.filter(ad => ad.id !== selectedAd.id));
      }
      
      // 広告費用データを再取得（削除を反映するため）
      await fetchBillingData();
      
      setDialogOpen(false);
      setLoading(false);
    } catch (err) {
      console.log('=== 広告削除エラー詳細 ===');
      console.log('エラー:', err);
      console.log('レスポンス:', err.response);
      
      // 楽観的描画：500エラーやネットワークエラーの場合は実際に削除されている可能性が高いため、
      // データ再取得を試みて、削除が成功している場合はエラーメッセージを表示しない
      if (err.response && (err.response.status === 500 || err.response.status >= 500)) {
        console.log('=== 500エラー - データ再取得を試行（削除確認） ===');
        try {
          // 広告データを再取得して実際に削除されているか確認
          const apiUrl = dialogAdType === 'areaTop' 
            ? `${config.API_BASE_URL}/api/corporate/ads/areatop`
            : `${config.API_BASE_URL}/api/corporate/ads/infeed`;
          
          const response = await axios.get(apiUrl, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (response.data && response.data.status === 'success') {
            // 削除対象の広告がリストに存在しないかチェック
            const adExists = response.data.ads.some(ad => ad.id === selectedAd.id);
            
            if (!adExists) {
              console.log('=== データ再取得成功 - 広告削除は実際に成功していました ===');
              
              // 画像URLを正規化して設定
              const normalizedAds = response.data.ads.map(ad => ({
                ...ad,
                imageUrl: normalizeImageUrl(ad.imageUrl)
              }));
              
              if (dialogAdType === 'areaTop') {
                setAreaTopAds(sortAdsByNewest(normalizedAds));
              } else {
                setInfeedAds(sortAdsByNewest(normalizedAds));
              }
              
              // 広告費用データを再取得
              await fetchBillingData();
              
              // 成功として扱い、エラーメッセージは表示しない
              setDialogOpen(false);
              setLoading(false);
              return; // エラー処理をスキップ
            }
          }
        } catch (retryErr) {
          console.log('=== データ再取得も失敗（削除確認） ===');
          console.log('再取得エラー:', retryErr);
        }
      }
      
      // 認証エラー（401/403）や400番台エラーの場合のみエラーメッセージを表示
      if (err.response && (err.response.status === 401 || err.response.status === 403 || (err.response.status >= 400 && err.response.status < 500))) {
        setError(`エラー (${err.response.status}): 広告の削除中にエラーが発生しました。`);
      } else if (!err.response) {
        // ネットワークエラーの場合は軽微なメッセージ
        setError('通信エラーが発生しましたが、削除は正常に完了している可能性があります。');
      } else {
        // その他のエラー（500番台など）は楽観的メッセージ
        setError('一時的なエラーが発生しましたが、削除は正常に完了している可能性があります。');
      }
      
      setLoading(false);
      setDialogOpen(false);
    }
  };

  // 費用詳細ダイアログを開く
  const handleOpenBillingDetail = (period) => {
    setSelectedBillingPeriod(period);
    setBillingDetailOpen(true);
  };

  // 費用詳細ダイアログを閉じる
  const handleCloseBillingDetail = () => {
    setBillingDetailOpen(false);
  };

  // エリアごとの最高単価を取得する関数
  const fetchHighestBid = async (areaCode) => {
    if (!areaCode) return;
    
    try {
              console.log('最高単価API呼び出し:', `${config.API_BASE_URL}/api/corporate/ads/highest-bid/${areaCode}`);
      
      // エリアコードにスラッシュが含まれている場合、URLエンコードする
      const encodedAreaCode = encodeURIComponent(areaCode);
      
              const response = await axios.get(`${config.API_BASE_URL}/api/corporate/ads/highest-bid/${encodedAreaCode}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`最高単価APIレスポンス (${areaCode}):`, response);
      
      if (response.data && response.data.status === 'success') {
        console.log(`最高単価設定 (${areaCode}):`, response.data.data);
        setHighestBids(prev => ({
          ...prev,
          [areaCode]: response.data.data
        }));
      }
    } catch (err) {
      console.error('=== 最高単価取得エラー詳細 ===');
      console.error('エリアコード:', areaCode);
      console.error('エラーオブジェクト:', err);
      console.error('エラーメッセージ:', err.message);
      console.error('エラーステータス:', err.response?.status);
      console.error('エラーステータステキスト:', err.response?.statusText);
      console.error('エラーレスポンスデータ:', err.response?.data);
      console.error('リクエストURL:', err.config?.url);
      console.error('=== 最高単価取得エラー詳細終了 ===');
      
      if (err.response) {
        console.error('HTTP応答エラー');
      } else if (err.request) {
        console.error('ネットワークエラー（応答なし）');
      } else {
        console.error('リクエスト設定エラー');
      }
    }
  };

  // useEffectでuserCitiesが更新されたときにも最高単価を取得
  useEffect(() => {
    const fetchHighestBidsForAllCities = async () => {
      if (userCities.length > 0 && token) {
        
        for (const city of userCities) {
          await fetchHighestBid(city.code);
        }
      }
    };
    
    fetchHighestBidsForAllCities();
  }, [userCities, token]);

  if (loading && !areaTopAds.length && !infeedAds.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // 日付フォーマット用の関数（YYYY年MM月DD日形式）
  const formatDateJP = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  return (
    <Box className="advertisements-container">
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          広告掲載管理
        </Typography>
        
        <Box display="flex" gap={2} className="billing-cards">
          <Card 
            sx={{ minWidth: 240, bgcolor: '#f8f9fa' }} 
            className="billing-card"
            onClick={() => handleOpenBillingDetail('current')}
          >
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                {billingData.current_month.is_confirmed ? '当月広告利用料（確定）' : '当月広告利用料（予定）'}
              </Typography>
              <Typography variant="h5" component="div" color="primary" className="billing-amount">
                {billingData.current_month.amount.toLocaleString()}円
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                {formatDateJP(billingData.current_month.period_start)} 〜 {formatDateJP(billingData.current_month.period_end)}
              </Typography>
              <Tooltip title="詳細を表示">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </CardContent>
          </Card>
          
          <Card 
            sx={{ minWidth: 240, bgcolor: '#f8f9fa' }} 
            className="billing-card"
            onClick={() => handleOpenBillingDetail('next')}
          >
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                次月広告利用料（予定）
              </Typography>
              <Typography variant="h5" component="div" color="primary" className="billing-amount">
                {billingData.next_month.amount.toLocaleString()}円
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                {formatDateJP(billingData.next_month.period_start)} 〜 {formatDateJP(billingData.next_month.period_end)}
              </Typography>
              <Tooltip title="詳細を表示">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </CardContent>
          </Card>
        </Box>
      </Box>
      
      <Paper className="ads-tabs-container">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="エリアトップ広告" />
          <Tab label="インフィード広告" />
        </Tabs>
        
        {/* エリアトップ広告 */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              エリアトップ広告一覧
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create', 'areaTop')}
            >
              新規作成
            </Button>
          </Box>
          
          {error && <Typography color="error" className="error-message">{error}</Typography>}
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>タイトル</TableCell>
                  <TableCell>エリア</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>掲載期間</TableCell>
                  <TableCell>時間帯</TableCell>
                  <TableCell>予算/単価</TableCell>
                  <TableCell>表示回数</TableCell>
                  <TableCell>クリック数</TableCell>
                  <TableCell>いいね数</TableCell>
                  <TableCell>アクション</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {areaTopAds.map((ad) => {
                  // 広告のステータスを計算
                  const adStatus = getAdStatus({
                    ...ad,
                    startDate: ad.startDate || ad.startTime,
                    endDate: ad.endDate || ad.endTime
                  });
                  
                  return (
                    <TableRow key={ad.id}>
                      <TableCell>{ad.title}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{getCityNameByCode(ad.area, userCities)}</Typography>
                          {highestBids[ad.area] && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              最高単価: {highestBids[ad.area].highest_area_top_bid}円
                            </Typography>
                          )}
                          {!highestBids[ad.area] && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              最高単価を取得中...
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${adStatus}`}>
                          {getStatusLabel(adStatus)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(ad.startDate || ad.startTime)} 〜 {formatDate(ad.endDate || ad.endTime)}
                      </TableCell>
                      <TableCell>
                        {ad.timeSlots?.length > 0 ? ad.timeSlots.length + '枠' : '未設定'}
                      </TableCell>
                      <TableCell>
                        {ad.budget?.toLocaleString()}円 / {ad.bidAmount}円
                      </TableCell>
                      <TableCell>{ad.impressions?.toLocaleString()}</TableCell>
                      <TableCell>{ad.clicks?.toLocaleString()}</TableCell>
                      <TableCell>{ad.likes?.toLocaleString()}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenDialog('edit', 'areaTop', ad)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title="次回期間に複製">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleDuplicateAd(ad)}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleOpenDialog('delete', 'areaTop', ad)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        {/* インフィード広告 */}
        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              インフィード広告一覧
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create', 'infeed')}
            >
              新規作成
            </Button>
          </Box>
          
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              インフィード広告の表示について
            </Typography>
            <Typography variant="body2">
              • 広告は一般の投稿5件ごとに表示されます<br />
              • 表示順序はクリック単価が高い広告が優先的に表示されます
            </Typography>
          </Paper>
          
          {error && <Typography color="error" className="error-message">{error}</Typography>}
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>タイトル</TableCell>
                  <TableCell>エリア</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>掲載期間</TableCell>
                  <TableCell>時間帯</TableCell>
                  <TableCell>予算/単価</TableCell>
                  <TableCell>表示回数</TableCell>
                  <TableCell>クリック数</TableCell>
                  <TableCell>いいね数</TableCell>
                  <TableCell>アクション</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {infeedAds.map((ad) => {
                  // 広告のステータスを計算
                  const adStatus = getAdStatus({
                    ...ad,
                    startDate: ad.startDate || ad.startTime,
                    endDate: ad.endDate || ad.endTime
                  });
                  
                  return (
                    <TableRow key={ad.id}>
                      <TableCell>{ad.title}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{getCityNameByCode(ad.area, userCities)}</Typography>
                          {highestBids[ad.area] && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              最高単価: {highestBids[ad.area].highest_infeed_bid}円
                            </Typography>
                          )}
                          {!highestBids[ad.area] && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              最高単価を取得中...
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${adStatus}`}>
                          {getStatusLabel(adStatus)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(ad.startDate || ad.startTime)} 〜 {formatDate(ad.endDate || ad.endTime)}
                      </TableCell>
                      <TableCell>
                        {ad.timeSlots?.length > 0 ? ad.timeSlots.length + '枠' : '未設定'}
                      </TableCell>
                      <TableCell>
                        {ad.budget?.toLocaleString()}円 / {ad.bidAmount}円
                      </TableCell>
                      <TableCell>{ad.impressions?.toLocaleString()}</TableCell>
                      <TableCell>{ad.clicks?.toLocaleString()}</TableCell>
                      <TableCell>{ad.likes?.toLocaleString()}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenDialog('edit', 'infeed', ad)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title="次回期間に複製">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleDuplicateAd(ad)}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleOpenDialog('delete', 'infeed', ad)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>
      
      {/* 広告作成・編集ダイアログ */}
      <Dialog open={dialogOpen && (dialogType === 'create' || dialogType === 'edit')} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'create' ? '新規広告作成' : '広告を編集'}
          {dialogAdType === 'areaTop' ? '（エリアトップ広告）' : '（インフィード広告）'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="タイトル"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                fullWidth
                required
                helperText={
                  <span style={{ 
                    color: isTitleOverLimit ? '#f4212e' : titleRemainingChars <= 5 ? '#ff9500' : '#536471' 
                  }}>
                    残り{titleRemainingChars}文字 ({titleCharCount}/{MAX_TITLE_LENGTH})
                  </span>
                }
                error={isTitleOverLimit}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="説明"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={3}
                helperText={
                  <span style={{ 
                    color: isDescriptionOverLimit ? '#f4212e' : descriptionRemainingChars <= 20 ? '#ff9500' : '#536471' 
                  }}>
                    残り{descriptionRemainingChars}文字 ({descriptionCharCount}/{MAX_DESCRIPTION_LENGTH})
                  </span>
                }
                error={isDescriptionOverLimit}
              />
            </Grid>
            
            {/* 画像アップロード */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                広告画像 *
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 1 }}
              >
                画像をアップロード
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              <FormHelperText>
                {dialogAdType === 'areaTop' 
                  ? '推奨サイズ：1200×600ピクセル (2:1)、最大2MB'
                  : '推奨サイズ：1200×600ピクセル (2:1)、最大2MB'}
              </FormHelperText>
              
              {imagePreview && (
                <Box className="image-preview" sx={{ mt: 2, maxWidth: 400, maxHeight: 210, overflow: 'hidden' }}>
                  <img 
                    src={normalizeImageUrl(imagePreview)} 
                    alt={formData.title || "広告プレビュー"} 
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="リンク先URL"
                name="targetUrl"
                value={formData.targetUrl}
                onChange={handleFormChange}
                fullWidth
                required
              />
            </Grid>
            
            {/* 時間帯選択（3時間区切り、複数選択可） */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="time-slots-label">掲載時間帯</InputLabel>
                <Select
                  labelId="time-slots-label"
                  id="time-slots"
                  multiple
                  value={formData.timeSlots}
                  onChange={handleTimeSlotChange}
                  input={<OutlinedInput label="掲載時間帯" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const timeSlot = TIME_SLOTS.find(slot => slot.value === value);
                        return (
                          <Chip key={value} label={timeSlot ? timeSlot.label : value} />
                        );
                      })}
                    </Box>
                  )}
                >
                  {TIME_SLOTS.map((slot) => (
                    <MenuItem key={slot.id} value={slot.value}>
                      {slot.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>3時間区切りの掲載希望時間帯を選択してください</FormHelperText>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="予算（円）"
                name="budget"
                type="number"
                value={formData.budget}
                InputProps={{
                  readOnly: dialogType === 'edit', // 編集時は読み取り専用
                  inputProps: { min: 1000 }
                }}
                onChange={handleFormChange}
                fullWidth
                required
                helperText="クリック課金の上限予算を設定してください"
              />
            </Grid>
            
            {dialogType === 'edit' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="予算を追加（円）"
                  name="additionalBudget"
                  type="number"
                  defaultValue={0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setFormData({
                      ...formData,
                      additionalBudget: value
                    });
                  }}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  helperText="追加する予算額を入力してください"
                />
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <TextField
                label="クリック単価（円）"
                name="bidAmount"
                type="number"
                value={formData.bidAmount}
                onChange={handleFormChange}
                fullWidth
                required
                InputProps={{
                  inputProps: { min: 10 }
                }}
                helperText="1クリックあたりの単価（高いほど優先表示されます）"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>ステータス</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  label="ステータス"
                >
                  <MenuItem value="active">公開中</MenuItem>
                  <MenuItem value="inactive">停止中</MenuItem>
                  <MenuItem value="deleted">削除済み</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {dialogAdType === 'areaTop' ? (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="area-select-label">エリア</InputLabel>
                  <Select
                    labelId="area-select-label"
                    id="area-select"
                    name="area"
                    value={formData.area}
                    onChange={handleFormChange}
                    label="エリア"
                  >
                    {userCities.map((city) => (
                      <MenuItem key={city.code} value={city.code}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formData.area && highestBids[formData.area] && (
                    <FormHelperText sx={{ color: 'text.secondary' }}>
                      このエリアの現在の最高単価: エリアトップ {highestBids[formData.area].highest_area_top_bid}円 / インフィード {highestBids[formData.area].highest_infeed_bid}円
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>エリア</InputLabel>
                    <Select
                      name="area"
                      value={formData.area}
                      onChange={handleFormChange}
                      label="エリア"
                    >
                      {userCities.map((city) => (
                        <MenuItem key={city.code} value={city.code}>
                          {city.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>登録している市区町村から選択してください</FormHelperText>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            キャンセル
          </Button>
          <Button 
            onClick={handleSaveAd} 
            variant="contained" 
            color="primary"
            disabled={loading || isTitleOverLimit || isDescriptionOverLimit}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 広告削除確認ダイアログ */}
      <Dialog open={dialogOpen && dialogType === 'delete'} onClose={handleCloseDialog}>
        <DialogTitle>
          広告を削除しますか？
        </DialogTitle>
        <DialogContent>
          <Typography>
            この操作は取り消せません。広告「{selectedAd?.title}」を削除してもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            キャンセル
          </Button>
          <Button onClick={handleDeleteAd} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 広告複製ダイアログ */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={closeDuplicateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>広告を複製</DialogTitle>
        <DialogContent>
          {duplicateAdData && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                次の期間で広告を複製します：
              </Typography>
              <Typography variant="body1" gutterBottom>
                {duplicateAdData.startDate && duplicateAdData.endDate ? (
                  <>
                    <strong>現在の掲載期間：</strong> {formatDate(duplicateAdData.startDate)} 〜 {formatDate(duplicateAdData.endDate)}<br />
                    <strong>次回の掲載期間：</strong> {formatDate(calculateNextAdPeriod(duplicateAdData.endDate).startDate)} 〜 {formatDate(calculateNextAdPeriod(duplicateAdData.endDate).endDate)}
                  </>
                ) : '掲載期間が取得できません'}
              </Typography>
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={inheritBudget}
                      onChange={(e) => setInheritBudget(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="掲載期間終了後の残予算を引き継ぐ"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  ※チェックを入れると、現在の広告の予算消費がリアルタイムで予約広告にも反映されます。また、掲載期間終了時に残った予算も自動的に引き継がれます。
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDuplicateDialog} color="secondary">
            キャンセル
          </Button>
          <Button
            onClick={executeDuplicateAd}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            複製する
            {loading && <CircularProgress size={24} className="buttonProgress" />}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 広告費用詳細ダイアログ */}
      <Dialog
        open={billingDetailOpen}
        onClose={handleCloseBillingDetail}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedBillingPeriod === 'current' ? 
            (billingData.current_month.is_confirmed ? '当月広告利用料（確定）' : '当月広告利用料（予定）') : 
            '次月広告利用料（予定）'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            期間: {formatDateJP(selectedBillingPeriod === 'current' ? 
              billingData.current_month.period_start : 
              billingData.next_month.period_start)} 〜 {formatDateJP(selectedBillingPeriod === 'current' ? 
              billingData.current_month.period_end : 
              billingData.next_month.period_end)}
          </Typography>
          
          <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
            合計: {(selectedBillingPeriod === 'current' ? 
              billingData.current_month.amount : 
              billingData.next_month.amount).toLocaleString()}円
          </Typography>
          
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>広告名</TableCell>
                  <TableCell>広告タイプ</TableCell>
                  <TableCell align="right">クリック単価</TableCell>
                  <TableCell align="right">クリック数</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>期間</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedBillingPeriod === 'current' ? 
                  billingData.current_month.detail : 
                  billingData.next_month.detail).map((ad) => (
                  <TableRow key={ad.id}>
                    <TableCell>{ad.title}</TableCell>
                    <TableCell>{ad.type}</TableCell>
                    <TableCell align="right">{ad.bid_amount?.toLocaleString()}円</TableCell>
                    <TableCell align="right">{ad.clicks?.toLocaleString()}</TableCell>
                    <TableCell align="right">{ad.amount?.toLocaleString()}円</TableCell>
                    <TableCell>
                      <span className={`status-badge ${ad.status}`}>
                        {getStatusLabel(ad.status)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {formatDate(ad.start_date)} 〜 {formatDate(ad.end_date)}
                    </TableCell>
                  </TableRow>
                ))}
                
                {(selectedBillingPeriod === 'current' ? 
                  billingData.current_month.detail : 
                  billingData.next_month.detail).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      表示する広告費用情報がありません
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontSize: '0.8rem' }}>
            ※広告費用はクリック単価×クリック数で計算されます<br />
            ※削除された広告も含まれています
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBillingDetail}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Advertisements; 